import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../app/auth";
import { useWorkspace } from "../app/workspace";
import { Icon } from "../components/icons";
import { Avatar } from "../components/ui";
import { shortDate } from "../lib/format";
import {
  getBilling, changePlan, getTeam, inviteMember, revokeInvite,
  type PlanCatalogItem, type Member, type Invite, type Plan,
} from "../lib/authClient";
import * as work from "../lib/workClient";
import * as email from "../lib/emailClient";
import { Compose } from "../components/Compose";
import { timeAgo } from "../lib/format";

type Tab = "billing" | "usage" | "mailbox" | "team" | "start";

export default function Settings() {
  const { me, setPlan } = useAuth();
  const [tab, setTab] = useState<Tab>("billing");

  return (
    <div className="page">
      <div className="mb-24">
        <p className="eyebrow">Settings</p>
        <h1 style={{ fontFamily: "var(--serif)", fontSize: 28, marginTop: 6 }}>{me?.workspace.name}</h1>
        <p className="page__intro" style={{ marginBottom: 0 }}>Everything is self-serve — change your plan, invite your team, and finish setup without talking to anyone.</p>
      </div>

      <div className="tabs" style={{ maxWidth: 640 }}>
        <button className={`tab ${tab === "billing" ? "active" : ""}`} onClick={() => setTab("billing")}>Plan &amp; billing</button>
        <button className={`tab ${tab === "usage" ? "active" : ""}`} onClick={() => setTab("usage")}>Usage &amp; limits</button>
        <button className={`tab ${tab === "mailbox" ? "active" : ""}`} onClick={() => setTab("mailbox")}>Mailbox</button>
        <button className={`tab ${tab === "team" ? "active" : ""}`} onClick={() => setTab("team")}>Team</button>
        <button className={`tab ${tab === "start" ? "active" : ""}`} onClick={() => setTab("start")}>Getting started</button>
      </div>

      {tab === "billing" && <Billing onPlan={setPlan} />}
      {tab === "usage" && <Usage />}
      {tab === "mailbox" && <MailboxTab meEmail={me?.user.email ?? ""} />}
      {tab === "team" && <Team />}
      {tab === "start" && <GettingStarted />}
    </div>
  );
}

/* ---------------- billing ---------------- */
function Billing({ onPlan }: { onPlan: (p: Plan) => void }) {
  const [plan, setPlanState] = useState<Plan | null>(null);
  const [catalog, setCatalog] = useState<PlanCatalogItem[]>([]);
  const [seatsUsed, setSeatsUsed] = useState(1);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => { getBilling().then((b) => { setPlanState(b.plan); setCatalog(b.catalog); setSeatsUsed(b.seatsUsed); }); }, []);

  const choose = async (id: string) => {
    setBusy(id);
    const p = await changePlan(id);
    setBusy(null);
    setPlanState(p);
    onPlan(p);
  };

  const priceLabel = (p: PlanCatalogItem) => (typeof p.priceMonthly !== "number" ? "Custom" : p.priceMonthly === 0 ? "$0" : `$${p.priceMonthly}`);
  const hasSeatPrice = (p: PlanCatalogItem) => typeof p.priceMonthly === "number" && p.priceMonthly > 0;

  return (
    <div style={{ display: "grid", gap: 20 }}>
      <div className="card">
        <div className="card__bd row between wrap" style={{ gap: 16 }}>
          <div>
            <div className="ff-mono faint" style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: ".08em" }}>Current plan</div>
            <div className="row" style={{ gap: 10, marginTop: 6 }}>
              <span style={{ fontFamily: "var(--serif)", fontSize: 24 }}>{plan?.name ?? "—"}</span>
              {plan?.status === "trialing" && <span className="badge badge--watch">Trial ends {plan.trialEndsAt ? shortDate(plan.trialEndsAt) : "soon"}</span>}
              {plan?.status === "active" && <span className="badge badge--good">Active</span>}
              {plan?.status === "free" && <span className="badge badge--neutral">Free</span>}
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div className="faint" style={{ fontSize: 12.5 }}>Seats</div>
            <div className="num" style={{ fontSize: 20, fontWeight: 600 }}>{seatsUsed} / {plan?.seatLimit ?? "—"}</div>
          </div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 14, maxWidth: 560 }}>
        {catalog.map((p) => {
          const current = plan?.id === p.id;
          return (
            <div key={p.id} className={`card plan-card ${current ? "plan-card--current" : ""}`}>
              <div className="card__bd">
                <div className="row between">
                  <b style={{ fontSize: 16 }}>{p.name}</b>
                  {current && <span className="badge badge--good">Current</span>}
                </div>
                <div style={{ fontFamily: "var(--serif)", fontSize: 30, marginTop: 8 }}>{priceLabel(p)}<span className="muted" style={{ fontSize: 13, fontFamily: "var(--sans)" }}>{hasSeatPrice(p) ? " / seat · mo" : ""}</span></div>
                <div className="faint" style={{ fontSize: 12.5, margin: "6px 0 16px" }}>Up to {p.seatLimit >= 999 ? "unlimited" : p.seatLimit} seats</div>
                <button
                  className={`btn ${current ? "" : "btn--primary"} btn--sm`}
                  style={{ width: "100%", justifyContent: "center", opacity: current ? 0.55 : 1 }}
                  disabled={current || busy === p.id}
                  onClick={() => choose(p.id)}
                >
                  {current ? "Current plan" : busy === p.id ? "Applying…" : p.priceMonthly === 0 ? "Downgrade to Free" : "Switch to Team"}
                </button>
              </div>
            </div>
          );
        })}
      </div>
      <p className="faint" style={{ fontSize: 12.5 }}>Upgrade or downgrade any time — changes apply immediately. No contracts, no sales calls. Billing runs on Stripe in production.</p>
    </div>
  );
}

/* ---------------- team ---------------- */
function Usage() {
  const { ws } = useWorkspace();
  const [mode, setMode] = useState<work.Mode>("local");
  const [ent, setEnt] = useState<work.Entitlement>(() => work.localEntitlement(ws));
  const [toast, setToast] = useState("");
  useEffect(() => { work.loadEntitlements(ws).then((r) => { setMode(r.mode); setEnt(r.ent); }); }, [ws]);
  const flash = (m: string) => { setToast(m); setTimeout(() => setToast(""), 2600); };
  const buy = async (kind: work.EntKind, label: string) => { const e = await work.buySlots(mode, ws, kind, 1); setEnt(e); flash(`Added a ${label} slot.`); };

  const meters: { kind?: work.EntKind; label: string; used: number; limit: number; note: string }[] = [
    { kind: "agents", label: "Risk agents", used: ent.agentsUsed, limit: ent.agentLimit, note: `$${ent.prices.agents}/mo per extra` },
    { kind: "automations", label: "Automations", used: ent.automationsUsed, limit: ent.automationLimit, note: `$${ent.prices.automations}/mo per extra` },
    { label: "AI analyses (this month)", used: ent.aiRunsUsed, limit: ent.aiRunsLimit, note: "Resets monthly · pauses at the limit, never an overage charge" },
  ];

  return (
    <div style={{ maxWidth: 760 }}>
      <div className="card">
        <div className="card__hd"><div><h3>Usage &amp; limits</h3><span className="sub">What your plan includes — buy more of what you need, no sales call</span></div></div>
        <div className="card__bd" style={{ display: "grid", gap: 18 }}>
          {toast && <div className="toast"><Icon name="check" size={14} /> {toast}</div>}
          {meters.map((m) => {
            const pctFull = Math.min(100, (m.used / Math.max(1, m.limit)) * 100);
            const at = m.used >= m.limit;
            return (
              <div key={m.label}>
                <div className="row between" style={{ marginBottom: 7 }}>
                  <b style={{ fontSize: 13.5 }}>{m.label}</b>
                  <span className="row" style={{ gap: 12 }}>
                    <span className="faint" style={{ fontSize: 12.5 }}><b style={{ color: at ? "var(--risk)" : "var(--ink)" }}>{m.used.toLocaleString()}</b> of {m.limit.toLocaleString()}</span>
                    {m.kind && <button className="btn btn--sm" onClick={() => buy(m.kind!, m.label.toLowerCase())}><Icon name="plus" size={13} /> Add slot</button>}
                  </span>
                </div>
                <div className="ent-bar"><span style={{ width: `${pctFull}%`, background: at ? "var(--risk)" : "var(--corona)" }} /></div>
                <div className="faint" style={{ fontSize: 11.5, marginTop: 6 }}>{m.note}</div>
              </div>
            );
          })}
        </div>
      </div>
      <p className="faint" style={{ fontSize: 12.5, marginTop: 12 }}>Add-on slots bill on Stripe in production and apply immediately. Roles &amp; permissions, SSO, governance and the API are included on every plan.</p>
    </div>
  );
}

function MailboxTab({ meEmail }: { meEmail: string }) {
  const [mode, setMode] = useState<email.Mode>("local");
  const [mailbox, setMailbox] = useState<email.Mailbox | null>(null);
  const [providers, setProviders] = useState<email.Providers | null>(null);
  const [outbox, setOutbox] = useState<email.OutboxEmail[]>([]);
  const [composing, setComposing] = useState(false);

  const refresh = () => email.loadMailbox().then((r) => { setMode(r.mode); setMailbox(r.mailbox); setProviders(r.providers); email.getOutbox(r.mode).then(setOutbox); });
  useEffect(() => { refresh(); }, []);

  const connect = (provider: email.MailProvider) => { const m = email.connect(mode, provider, meEmail); if (m) { setMailbox(m); } };
  const disconnect = async () => { await email.disconnect(mode); setMailbox(null); };

  return (
    <div style={{ maxWidth: 760 }}>
      {composing && <Compose onClose={() => setComposing(false)} onSent={() => email.getOutbox(mode).then(setOutbox)} />}
      <div className="card">
        <div className="card__hd"><div><h3>Connected mailbox</h3><span className="sub">Email customers from your own inbox — replies come back to you, not a no-reply address</span></div></div>
        <div className="card__bd">
          {mailbox ? (
            <div className="row between" style={{ alignItems: "center" }}>
              <div className="row" style={{ gap: 12 }}>
                <span className="n-ic" style={{ width: 40, height: 40, borderRadius: 10, display: "grid", placeItems: "center", background: "var(--surface-2)", border: "1px solid var(--line)" }}><Icon name="mail" size={18} /></span>
                <div>
                  <b style={{ fontSize: 14 }}>{mailbox.email}</b>
                  <div className="faint" style={{ fontSize: 12.5 }}>{mailbox.provider === "gmail" ? "Gmail" : "Microsoft 365 / Exchange"} · connected {timeAgo(mailbox.connectedAt)} ago{mailbox.simulated ? " · demo mode" : ""}</div>
                </div>
              </div>
              <div className="row" style={{ gap: 8 }}>
                <button className="btn btn--primary btn--sm" onClick={() => setComposing(true)}><Icon name="mail" size={14} /> Compose</button>
                <button className="btn btn--sm" onClick={disconnect}>Disconnect</button>
              </div>
            </div>
          ) : (
            <>
              <p className="muted" style={{ fontSize: 13.5, marginTop: 0 }}>Link a mailbox to send 1:1 email from Penumbrai. We never send mass mail on your behalf.</p>
              <div className="row" style={{ gap: 10, marginTop: 6 }}>
                <button className="auth__sso" style={{ flex: 1 }} onClick={() => connect("gmail")}><Icon name="mail" size={16} /> Connect Gmail{providers && !providers.gmail.configured ? " (demo)" : ""}</button>
                <button className="auth__sso" style={{ flex: 1 }} onClick={() => connect("microsoft")}><Icon name="mail" size={16} /> Connect Microsoft 365{providers && !providers.microsoft.configured ? " (demo)" : ""}</button>
              </div>
            </>
          )}
        </div>
      </div>

      {mailbox && (
        <div className="card" style={{ marginTop: 16 }}>
          <div className="card__hd"><h3>Outbox</h3><span className="tag">{outbox.length}</span></div>
          <div className="card__bd">
            {outbox.length === 0 ? <p className="faint" style={{ fontSize: 13 }}>Nothing sent yet.</p> : (
              <div style={{ display: "grid", gap: 8 }}>
                {outbox.slice(0, 10).map((e) => (
                  <div key={e.id} className="row between" style={{ padding: "9px 0", borderBottom: "1px solid var(--line-soft)" }}>
                    <div style={{ minWidth: 0 }}>
                      <b style={{ fontSize: 13 }}>{e.subject}</b>
                      <div className="faint" style={{ fontSize: 12 }}>to {e.to}</div>
                    </div>
                    <span className="row" style={{ gap: 8 }}>
                      <span className={`badge ${e.status === "sent" ? "badge--good" : "badge--risk"}`}>{e.status}{e.simulated ? " · demo" : ""}</span>
                      <span className="faint" style={{ fontSize: 11.5 }}>{timeAgo(e.at)}</span>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      <p className="faint" style={{ fontSize: 12.5, marginTop: 12 }}>Uses your provider's API (Gmail / Microsoft Graph) with send-only scopes. Configure OAuth to send for real; otherwise sends are recorded here in demo mode.</p>
    </div>
  );
}

function Team() {
  const [members, setMembers] = useState<Member[]>([]);
  const [invites, setInvites] = useState<Invite[]>([]);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("member");
  const [busy, setBusy] = useState(false);

  useEffect(() => { getTeam().then((t) => { setMembers(t.members); setInvites(t.invites); }); }, []);

  const invite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) return;
    setBusy(true);
    const inv = await inviteMember(email, role);
    setBusy(false);
    setInvites((x) => [...x, inv]);
    setEmail("");
  };
  const revoke = async (id: string) => { await revokeInvite(id); setInvites((x) => x.filter((i) => i.id !== id)); };

  return (
    <div style={{ display: "grid", gap: 20 }}>
      <div className="card">
        <div className="card__hd"><div><h3>Invite your team</h3><span className="sub">Anyone with the link joins your workspace — no admin approval needed</span></div></div>
        <div className="card__bd">
          <form className="row" style={{ gap: 10 }} onSubmit={invite}>
            <input className="input grow" type="email" placeholder="teammate@company.com" value={email} onChange={(e) => setEmail(e.target.value)} />
            <select className="input" value={role} onChange={(e) => setRole(e.target.value)}>
              <option value="member">Member</option>
              <option value="admin">Admin</option>
            </select>
            <button className="btn btn--primary" type="submit" disabled={busy}><Icon name="plus" size={15} /> Invite</button>
          </form>
        </div>
      </div>

      <div className="card">
        <div className="card__hd"><h3>Members</h3><span className="tag">{members.length}</span></div>
        <div className="card__bd" style={{ padding: 8 }}>
          {members.map((m) => (
            <div key={m.email} className="feed-item" style={{ gridTemplateColumns: "auto 1fr auto", alignItems: "center" }}>
              <Avatar name={m.name ?? m.email} size="sm" />
              <div><b style={{ fontSize: 13.5 }}>{m.name ?? m.email}</b><small>{m.email}</small></div>
              <span className="chip">{m.role}</span>
            </div>
          ))}
        </div>
      </div>

      {invites.length > 0 && (
        <div className="card">
          <div className="card__hd"><h3>Pending invites</h3><span className="tag">{invites.length}</span></div>
          <div className="card__bd" style={{ padding: 8 }}>
            {invites.map((i) => (
              <div key={i.id} className="feed-item" style={{ gridTemplateColumns: "1fr auto auto", alignItems: "center", gap: 12 }}>
                <div><b style={{ fontSize: 13.5 }}>{i.email}</b><small>{i.role} · invited</small></div>
                <span className="badge badge--watch">pending</span>
                <button className="btn btn--ghost btn--sm" onClick={() => revoke(i.id)}>Revoke</button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------- getting started ---------------- */
function GettingStarted() {
  const steps = useMemo(() => [
    { done: true, title: "Create your workspace", detail: "Done — you're in.", to: "" },
    { done: false, title: "Connect a data source", detail: "Sync accounts and usage from your warehouse, CRM, or product.", to: "/integrations" },
    { done: false, title: "Model your data", detail: "Add the objects and fields that match your business.", to: "/data" },
    { done: false, title: "Build a dashboard", detail: "Report on anything — one filter bar, every object.", to: "/dashboards" },
    { done: false, title: "Invite your team", detail: "Bring your CSMs into the workspace.", to: "/settings" },
  ], []);
  const done = steps.filter((s) => s.done).length;

  return (
    <div style={{ display: "grid", gap: 16, maxWidth: 680 }}>
      <div className="card">
        <div className="card__hd">
          <div><h3>Get to first value</h3><span className="sub">{done} of {steps.length} complete</span></div>
          <div className="meter" style={{ width: 160 }}><i style={{ width: `${(done / steps.length) * 100}%`, background: "var(--corona)" }} /></div>
        </div>
        <div className="card__bd" style={{ display: "grid", gap: 6 }}>
          {steps.map((s) => (
            <div key={s.title} className="row" style={{ gap: 12, padding: "10px 8px", borderBottom: "1px solid var(--line-soft)" }}>
              <span style={{ width: 22, height: 22, borderRadius: "50%", display: "grid", placeItems: "center", flex: "none", background: s.done ? "var(--good-soft)" : "var(--surface-2)", color: s.done ? "var(--good)" : "var(--faint)", border: `1px solid ${s.done ? "rgba(63,143,99,.35)" : "var(--line)"}` }}>
                {s.done ? <Icon name="check" size={13} /> : ""}
              </span>
              <div className="grow">
                <b style={{ fontSize: 13.5 }}>{s.title}</b>
                <small className="muted" style={{ display: "block", fontSize: 12.5 }}>{s.detail}</small>
              </div>
              {s.to && <Link to={s.to} className="btn btn--ghost btn--sm">{s.done ? "Review" : "Start"} <Icon name="arrowRight" size={13} /></Link>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
